import { SubjectGetPaging } from "services/subjectService";
import { useEffect, useState, Fragment } from "react";
import { Subject } from "models/model";
import { Dialog, Transition } from '@headlessui/react'
import ErrorModal from "popup/ErrorModal"

interface SaveSubjectModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: any) => Promise<void>;
    initialData?: any;
    licenseClassId?: string;
    courseId?: string;
    existingSubjects: string[];
}

export default function SaveSubjectModal({
                                             isOpen,
                                             onClose,
                                             onSubmit,
                                             initialData,
                                             licenseClassId,
                                             courseId,
                                             existingSubjects = []
                                         }: SaveSubjectModalProps) {
    const [formData, setFormData] = useState<any>({
        subjectName: '',
        subjectType: undefined,
        theoryHours: 0,
        practiceHours: 0,
        openRoadPracticeHours: 0,
        roadPracticeHours: 0,
        testHours: 0,
        notes: '',
    });

    const [subjects, setSubjects] = useState<Subject[]>([]);
    const [filteredSubjects, setFilteredSubjects] = useState<Subject[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (isOpen) {
            fetchSubjects();
            if (initialData) {
                setFormData({
                    subjectName: initialData.subjectName || '',
                    subjectType: initialData.subjectType || undefined,
                    theoryHours: initialData.theoryHours || 0,
                    practiceHours: initialData.practiceHours || 0,
                    openRoadPracticeHours: initialData.openRoadPracticeHours || 0,
                    roadPracticeHours: initialData.roadPracticeHours || 0,
                    testHours: initialData.testHours || 0,
                    notes: initialData.notes || '',
                    subjectId: initialData.subjectId || null
                });
            } else {
                setFormData({
                    subjectName: '',
                    subjectType: undefined,
                    theoryHours: 0,
                    practiceHours: 0,
                    openRoadPracticeHours: 0,
                    roadPracticeHours: 0,
                    testHours: 0,
                    notes: ''
                });
            }
        }
    }, [isOpen, initialData]);

    const fetchSubjects = async () => {
        try {
            const response = await SubjectGetPaging({ pageIndex: 1, pageSize: 100 });
            if (response.result?.items) {
                setSubjects(response.result.items);
            }
        } catch (error) {
            console.error("Failed to fetch subjects", error);
            setErrorMessage("Không thể tải danh sách môn học");
            setErrorModalOpen(true);
        }
    };

    const handleSubjectNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFormData({ ...formData, subjectName: value, subjectId: null });

        // Hiển thị gợi ý nếu có nội dung nhập
        if (value.length > 0) {
            filterSubjects(value);
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }
    };

    const filterSubjects = (searchText: string) => {
        let filtered = subjects.filter(subject =>
            !existingSubjects.includes(subject.name) &&
            subject.name.toLowerCase().includes(searchText.toLowerCase())
        );

        if (formData.subjectType) {
            filtered = filtered.filter(subject =>
                subject.type == parseInt(formData.subjectType)
            );
        }

        setFilteredSubjects(filtered);
    };

    const selectSubject = (subject: Subject) => {
        setFormData({
            ...formData,
            subjectName: subject.name,
            subjectType: subject.type,
            subjectId: subject.id
        });
        setShowSuggestions(false);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        const newFormData = {
            ...formData,
            [name]: name.includes('Hours') ? parseInt(value) || 0 : value
        };

        setFormData(newFormData);

        // Nếu thay đổi loại môn học, filter lại danh sách gợi ý nếu đang hiển thị
        if (name === 'subjectType' && showSuggestions && formData.subjectName) {
            filterSubjects(formData.subjectName);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const dataToSubmit = {
                ...formData,
                subjectType:parseInt(formData.subjectType),
                licenseClassId,
                courseId,
                id: initialData?.id || null
            };
            await onSubmit(dataToSubmit);
            onClose();
        } catch (error: any) {
            const errorMsg = error?.responseException?.exceptionMessage
                || error?.message
                || 'Đã xảy ra lỗi khi lưu môn học';
            setErrorMessage(errorMsg);
            setErrorModalOpen(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Transition appear show={isOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={onClose}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-lg font-semibold leading-6 text-gray-900"
                                    >
                                        {initialData ? 'Cập nhật môn học' : 'Thêm môn học mới'}
                                    </Dialog.Title>

                                    <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="relative">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Tên môn học *</label>
                                                <input
                                                    type="text"
                                                    name="subjectName"
                                                    value={formData.subjectName}
                                                    onChange={handleSubjectNameChange}
                                                    onFocus={() => {
                                                        if (subjects.length > 0) {
                                                            filterSubjects(formData.subjectName);
                                                            setShowSuggestions(true);
                                                        }
                                                    }}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    required
                                                    disabled={loading}
                                                />
                                                {showSuggestions && filteredSubjects.length > 0 && (
                                                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                                        {filteredSubjects.map((subject) => (
                                                            <div
                                                                key={subject.id}
                                                                className="p-2 hover:bg-gray-100 cursor-pointer"
                                                                onClick={() => selectSubject(subject)}
                                                            >
                                                                {subject.name}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Loại môn học *</label>
                                                <select
                                                    name="subjectType"
                                                    value={formData.subjectType || ''}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    required
                                                    disabled={loading}
                                                >
                                                    <option value="">Chọn loại</option>
                                                    <option value="1">Lý thuyết</option>
                                                    <option value="2">Thực hành</option>
                                                </select>
                                            </div>
                                        </div>

                                        {/* Phần còn lại giữ nguyên */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Giờ lý thuyết</label>
                                                <input
                                                    type="number"
                                                    name="theoryHours"
                                                    value={formData.theoryHours}
                                                    onChange={handleChange}
                                                    min="0"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    disabled={loading}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Giờ thực hành</label>
                                                <input
                                                    type="number"
                                                    name="practiceHours"
                                                    value={formData.practiceHours}
                                                    onChange={handleChange}
                                                    min="0"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    disabled={loading}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">TH trong hình</label>
                                                <input
                                                    type="number"
                                                    name="openRoadPracticeHours"
                                                    value={formData.openRoadPracticeHours}
                                                    onChange={handleChange}
                                                    min="0"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    disabled={loading}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">TH trên đường</label>
                                                <input
                                                    type="number"
                                                    name="roadPracticeHours"
                                                    value={formData.roadPracticeHours}
                                                    onChange={handleChange}
                                                    min="0"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    disabled={loading}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Giờ kiểm tra</label>
                                                <input
                                                    type="number"
                                                    name="testHours"
                                                    value={formData.testHours}
                                                    onChange={handleChange}
                                                    min="0"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Ghi chú</label>
                                            <textarea
                                                name="notes"
                                                value={formData.notes}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                rows={3}
                                                disabled={loading}
                                            />
                                        </div>

                                        <div className="flex justify-end pt-4 space-x-3">
                                            <button
                                                type="button"
                                                onClick={onClose}
                                                disabled={loading}
                                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                                            >
                                                Hủy
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={loading}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {loading ? (
                                                    <span className="flex items-center">
                                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        {initialData ? 'Đang cập nhật...' : 'Đang thêm...'}
                                                    </span>
                                                ) : initialData ? 'Cập nhật' : 'Thêm mới'}
                                            </button>
                                        </div>
                                    </form>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            <ErrorModal
                isOpen={errorModalOpen}
                message={errorMessage}
                onClose={() => setErrorModalOpen(false)}
            />
        </>
    );
}